import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Text from '@/components/Typography/Text';
import { PrimaryCta, StoreBadges, useLanding } from './LandingKit';
import LivingPixelScene from './LivingPixelScene';
import { content } from '@/lib/landingPageSeo';
import { LAYOUT, fluid, isWeb } from './landingTokens';

/** Illustrated scenery and independent atmosphere layers give the hero depth. */
function HeroAtmosphere() {
  const { isDark, width } = useLanding();
  const stackedScene = width < 1100;
  return (
    <View pointerEvents="none" aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={[StyleSheet.absoluteFillObject, { backgroundColor: isDark ? '#111A17' : '#DCEEF0' }]}>
      <View style={{ position: 'absolute', left: -24, right: -24, bottom: -12, height: stackedScene ? 660 : '104%' }}>
        <LivingPixelScene />
        {stackedScene && <LinearGradient colors={['#DCEEF0', 'rgba(220,238,240,0)']} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 240 }} />}
      </View>
      <LinearGradient
        colors={isDark ? ['rgba(9,20,16,.88)', 'rgba(9,20,16,.7)', 'rgba(9,20,16,.25)'] : ['rgba(235,247,245,.38)', 'rgba(235,247,245,.18)', 'rgba(255,250,239,0)']}
        locations={[0, 0.48, 1]}
        style={StyleSheet.absoluteFillObject}
      />
      <LinearGradient colors={isDark ? ['rgba(21,29,24,0)', '#151D18'] : ['rgba(240,243,236,0)', '#F0F3EC']} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: stackedScene ? 60 : 120 }} />
    </View>
  );
}

function LandingEditorialHero({ topInset = 0 }) {
  const { c, width, pad, display, prose } = useLanding();
  const headline = display({ min: 38, max: 80, leading: 1.06, tracking: -0.055 });

  return (
    <View accessibilityLabel="Gab AI" style={{ width: '100%', alignItems: 'center', backgroundColor: c.page, position: 'relative', overflow: 'hidden' }}>
      <HeroAtmosphere />
      <View style={{ width: '100%', maxWidth: LAYOUT.max, paddingHorizontal: pad, paddingTop: topInset + fluid(width, 112, 144), paddingBottom: width < 1100 ? 280 : 220, alignItems: 'center' }}>
        <View style={{ alignItems: 'center', width: '100%' }}>
          <Text weight="semibold" style={{ color: c.accent, fontSize: 15, marginBottom: 20 }}>{content.kicker}</Text>
          <Text tag="h1" weight="semibold" style={{ color: c.ink, ...headline, textAlign: 'center' }}>
            AI that works.
            <Text tag="span" weight="semibold" style={{ color: c.accent, ...headline, ...(isWeb ? { display: 'block' } : {}) }}>{!isWeb ? '\n' : ' '}Your ideas, made real.</Text>
          </Text>
          <Text tag="p" style={{ color: c.inkSoft, ...prose({ min: 17, max: 20, leading: 1.5 }), textAlign: 'center', maxWidth: 560, marginTop: 24, ...(isWeb ? { textWrap: 'balance' } : {}) }}>
            {content.intro}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 28 }}>
            <PrimaryCta tone="invert" label="Start free" />
          </View>
        </View>
        <View style={{ alignItems: 'center', gap: 16, marginTop: fluid(width, 30, 40), padding: width < 700 ? 16 : 20, width: '100%', borderRadius: 22, backgroundColor: c.isDark ? 'rgba(16,25,20,.9)' : 'rgba(255,252,245,.9)', borderWidth: 1, borderColor: c.rule, maxWidth: 760, ...(isWeb ? { backdropFilter: 'blur(12px)' } : {}) }}>
          <Text weight="medium" style={{ color: c.inkSoft, fontSize: 13, textAlign: 'center' }}>On your Mac. On iOS and Android. In your browser.</Text>
          <StoreBadges centered showExtension scale={0.9} style={{ justifyContent: 'center' }} />
        </View>
      </View>
    </View>
  );
}

export default React.memo(LandingEditorialHero);
